import { useState } from "react";
import { ALL } from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { buildSearchQuery } from "@miplatform/utils";
import { useHomes } from "@mibuildweb/hooks";
import { FilterObject, GetHomesParams } from "../types";

const DEFAULT_SORT = "address/addressLine1 asc";
const GROUP_BY_SORT_MAP: Record<string, string> = {
  builder: "builder asc",
  latestTemplateName: "latestTemplateName asc",
  phase: "phase asc",
};
const SORT_BY_SORT_MAP: Record<string, string> = {
  startDate: "modifiedOn desc",
  closingDate: "closingDate asc",
};

export const useHomesViewModel = (initialSearch = "") => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [search, setSearch] = useState<string>(initialSearch);
  const debouncedSearch = useDebounce(search);

  const {
    getHomes: fetchHomes,
    homes: homesListData,
    setHomes: setHomesListData,
    deleteHome,
  } = useHomes();

  const getHomes = (params: GetHomesParams = {}) => {
    const { filters, skip, top } = params;
    const searchQuery = buildSearchQuery(search, ["address/addressLine1"]);
    const filterObj: FilterObject = { ...searchQuery };
    const sort = [
      filters?.groupBy && GROUP_BY_SORT_MAP[filters.groupBy],
      filters?.sortBy && SORT_BY_SORT_MAP[filters.sortBy],
      DEFAULT_SORT,
      "createdOn asc",
      "id asc", // tiebreaker to ensure consistent ordering
    ]
      .filter(
        (sort, index, sortOptions): sort is string =>
          Boolean(sort) && sortOptions.indexOf(sort) === index
      )
      .join(",");

    if (filters) {
      if (filters.builderId && filters.builderId !== ALL) {
        filterObj.builderId = {
          type: "guid",
          value: filters.builderId,
        };
      }
      if (filters.communityId && filters.communityId !== ALL) {
        filterObj.communityId = {
          type: "guid",
          value: filters.communityId,
        };
      }

      if (filters.phase && filters.phase !== ALL) {
        filterObj.phase = {
          eq: filters.phase,
        };
      }
      if (filters.latestTemplateName && filters.latestTemplateName !== ALL) {
        filterObj.latestTemplateName = {
          contains: filters.latestTemplateName,
        };
      }
    }

    return fetchHomes({
      skip,
      top,
      filters: filterObj,
      sort,
    });
  };

  return {
    isLoading,
    homesListData,
    stateManager,
    getHomes,
    setHomesListData,
    search,
    setSearch,
    debouncedSearch,
    handleHomeDeletion: deleteHome,
  };
};
